import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { formatDateTime, cn } from '@/lib/utils';

const PAGE_SIZE = 50;

const ACTION_COLORS: Record<string, string> = {
  qty_adjustment:      'bg-blue-100 text-blue-700',
  component_created:   'bg-green-100 text-green-700',
  component_updated:   'bg-cyan-100 text-cyan-700',
  component_deleted:   'bg-red-100 text-red-700',
  reservation_created: 'bg-purple-100 text-purple-700',
  reservation_cancelled: 'bg-orange-100 text-orange-700',
};

const ENTITY_COLORS: Record<string, string> = {
  component:   'bg-slate-100 text-slate-700',
  reservation: 'bg-violet-100 text-violet-700',
  build:       'bg-amber-100 text-amber-700',
  order:       'bg-teal-100 text-teal-700',
};

function getActionColor(action: string) {
  return ACTION_COLORS[action] ?? 'bg-gray-100 text-gray-700';
}

function getEntityColor(entity: string) {
  return ENTITY_COLORS[entity] ?? 'bg-gray-100 text-gray-700';
}

function formatAction(action: string) {
  return action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function DiffDisplay({ oldVal, newVal, action }: { oldVal: any; newVal: any; action: string }) {
  if (!oldVal && !newVal) return null;

  if (action === 'qty_adjustment' && oldVal && newVal) {
    const availChanged = oldVal.available_qty !== newVal.available_qty;
    const orderChanged = oldVal.on_order_qty !== newVal.on_order_qty;
    if (!availChanged && !orderChanged) return null;
    return (
      <div className="text-xs font-mono text-muted-foreground mt-1 space-x-4">
        {availChanged && (
          <span>Avail: {oldVal.available_qty} → <strong className="text-foreground">{newVal.available_qty}</strong></span>
        )}
        {orderChanged && (
          <span>On Order: {oldVal.on_order_qty} → <strong className="text-foreground">{newVal.on_order_qty}</strong></span>
        )}
      </div>
    );
  }

  // Generic diff: show changed keys
  if (oldVal && newVal) {
    const allKeys = [...new Set([...Object.keys(oldVal), ...Object.keys(newVal)])];
    const changed = allKeys.filter(k => JSON.stringify(oldVal[k]) !== JSON.stringify(newVal[k]));
    if (!changed.length) return null;
    return (
      <div className="text-xs font-mono text-muted-foreground mt-1 space-y-0.5">
        {changed.map(k => (
          <div key={k}>
            <span className="text-muted-foreground">{k}: </span>
            <span className="line-through text-red-400">{String(oldVal[k] ?? '—')}</span>
            {' → '}
            <strong className="text-foreground">{String(newVal[k] ?? '—')}</strong>
          </div>
        ))}
      </div>
    );
  }

  if (newVal) {
    return (
      <div className="text-xs font-mono text-muted-foreground mt-1 space-y-0.5">
        {Object.entries(newVal).filter(([, v]) => v !== null && v !== '').map(([k, v]) => (
          <div key={k}><span className="text-muted-foreground">{k}: </span><strong>{String(v)}</strong></div>
        ))}
      </div>
    );
  }

  return null;
}

export function LogsPage() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Fetch all logs (we do client-side filtering for simplicity, up to 500 records)
  const { data: allLogs, isLoading } = useQuery({
    queryKey: ['audit-log'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_log')
        .select('id, created_at, user_id, user_email, action, entity_type, entity_id, entity_label, old_value, new_value, notes')
        .order('created_at', { ascending: false })
        .limit(2000);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30 * 1000,
  });

  // Distinct values for filter dropdowns
  const distinctActions = useMemo(() => {
    if (!allLogs) return [];
    return [...new Set(allLogs.map((l: any) => l.action))].sort();
  }, [allLogs]);

  const distinctEntities = useMemo(() => {
    if (!allLogs) return [];
    return [...new Set(allLogs.map((l: any) => l.entity_type))].sort();
  }, [allLogs]);

  // Client-side filtering
  const filtered = useMemo(() => {
    if (!allLogs) return [];
    return allLogs.filter((l: any) => {
      if (filterAction && l.action !== filterAction) return false;
      if (filterEntity && l.entity_type !== filterEntity) return false;
      if (filterDateFrom && l.created_at < filterDateFrom) return false;
      if (filterDateTo && l.created_at > filterDateTo + 'T23:59:59') return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = [l.user_email, l.entity_label, l.entity_id, l.notes, l.action].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allLogs, filterAction, filterEntity, filterDateFrom, filterDateTo, search]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const clearFilters = () => {
    setSearch('');
    setFilterAction('');
    setFilterEntity('');
    setFilterDateFrom('');
    setFilterDateTo('');
    setPage(0);
  };

  const hasFilters = search || filterAction || filterEntity || filterDateFrom || filterDateTo;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <History className="h-6 w-6 text-muted-foreground" />
        <h1 className="text-3xl font-bold">Audit Log</h1>
        {allLogs && (
          <Badge variant="outline" className="ml-auto">{allLogs.length} total entries</Badge>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search user, label, ID…"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(0); }}
                className="pl-9 pr-9"
              />
              {search && (
                <button onClick={() => { setSearch(''); setPage(0); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <select
              value={filterAction}
              onChange={e => { setFilterAction(e.target.value); setPage(0); }}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">All actions</option>
              {distinctActions.map(a => (
                <option key={a} value={a}>{formatAction(a)}</option>
              ))}
            </select>
            <select
              value={filterEntity}
              onChange={e => { setFilterEntity(e.target.value); setPage(0); }}
              className="h-10 px-3 rounded-md border border-input bg-background text-sm"
            >
              <option value="">All entities</option>
              {distinctEntities.map(e => (
                <option key={e} value={e}>{e}</option>
              ))}
            </select>
            <Input
              type="date"
              value={filterDateFrom}
              onChange={e => { setFilterDateFrom(e.target.value); setPage(0); }}
              className="text-sm"
              placeholder="From date"
            />
            <div className="flex gap-2">
              <Input
                type="date"
                value={filterDateTo}
                onChange={e => { setFilterDateTo(e.target.value); setPage(0); }}
                className="text-sm flex-1"
                placeholder="To date"
              />
              {hasFilters && (
                <Button variant="ghost" size="icon" onClick={clearFilters} title="Clear filters">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          {filtered.length !== (allLogs?.length ?? 0) && (
            <p className="text-sm text-muted-foreground mt-2">
              Showing {filtered.length} of {allLogs?.length} entries
            </p>
          )}
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : paginated.length === 0 ? (
            <p className="text-muted-foreground text-center py-12">No log entries found</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium w-40">Timestamp</th>
                    <th className="text-left px-4 py-3 font-medium w-32">Action</th>
                    <th className="text-left px-4 py-3 font-medium w-28">Entity</th>
                    <th className="text-left px-4 py-3 font-medium">Label / ID</th>
                    <th className="text-left px-4 py-3 font-medium">Changes</th>
                    <th className="text-left px-4 py-3 font-medium w-40">User</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginated.map((entry: any) => (
                    <tr key={entry.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(entry.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap', getActionColor(entry.action))}>
                          {formatAction(entry.action)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', getEntityColor(entry.entity_type))}>
                          {entry.entity_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-sm">{entry.entity_label || '—'}</p>
                        {entry.entity_id && (
                          <p className="text-xs text-muted-foreground font-mono">{entry.entity_id}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        <DiffDisplay
                          oldVal={entry.old_value}
                          newVal={entry.new_value}
                          action={entry.action}
                        />
                        {entry.notes && (
                          <p className="text-xs text-muted-foreground italic mt-0.5">"{entry.notes}"</p>
                        )}
                        {!entry.old_value && !entry.new_value && !entry.notes && (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {entry.user_email || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page + 1} of {totalPages} · {filtered.length} entries
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
